import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import UndoIcon from '@mui/icons-material/Undo';
import PreviewIcon from '@mui/icons-material/Preview';
import { ThemeEditor } from './ThemeEditor';
import { ContentEditor } from './ContentEditor';
import { ImageUploader } from './ImageUploader';
import { CustomCSSEditor } from './CustomCSSEditor';
import { SiteCustomizationPreview } from './SiteCustomizationPreview';
import { VersionHistoryViewer } from './VersionHistoryViewer';
import { ApiService } from '../services/api';
import { invalidateSiteSettingsCache } from './ThemeProvider';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customization-tabpanel-${index}`}
      aria-labelledby={`customization-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `customization-tab-${index}`,
    'aria-controls': `customization-tabpanel-${index}`,
  };
}

export interface SiteSettings {
  id: string;
  version: string;
  theme: ThemeSettings;
  content: ContentSettings;
  images: ImageSettings;
  customCSS?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  publishedBy?: string;
}

export interface ThemeSettings {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  gradient?: {
    enabled: boolean;
    startColor: string;
    endColor: string;
    angle: number;
  };
  fonts: {
    heading: string;
    body: string;
    headingSize: number;
    bodySize: number;
  };
}

export interface ContentSettings {
  welcome: {
    title: string;
    message: string;
    tagline: string;
  };
  footer: {
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    copyrightText?: string;
    socialLinks: {
      facebook?: string;
      instagram?: string;
      twitter?: string;
      custom?: Array<{
        name: string;
        url: string;
        icon?: string;
      }>;
    };
  };
}

export interface ImageSettings {
  logo?: {
    url: string;
    alt: string;
  };
  hero?: {
    url: string;
    alt: string;
  };
  background?: {
    url: string;
    opacity: number;
  };
}

const DEFAULT_SETTINGS: SiteSettings = {
  id: 'draft',
  version: '1.0.0',
  theme: {
    colors: {
      primary: '#1976d2',
      secondary: '#dc004e',
      accent: '#f50057',
      background: '#ffffff',
      text: '#000000'
    },
    gradient: {
      enabled: false,
      startColor: '#667eea',
      endColor: '#764ba2',
      angle: 135
    },
    fonts: {
      heading: 'Roboto',
      body: 'Roboto',
      headingSize: 24,
      bodySize: 16
    }
  },
  content: {
    welcome: {
      title: 'Welcome to Our Event',
      message: 'We\'re excited to have you join us!',
      tagline: 'Making Memories Together'
    },
    footer: {
      contactEmail: 'info@example.com',
      copyrightText: `© ${new Date().getFullYear()} All Rights Reserved`,
      socialLinks: {}
    }
  },
  images: {},
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

export const SiteCustomizationManager: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftSettings, setDraftSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [publishedSettings, setPublishedSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getSiteSettings();
      
      if (response.success) {
        setDraftSettings(response.data.draft || DEFAULT_SETTINGS);
        setPublishedSettings(response.data.published || DEFAULT_SETTINGS);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
      // Use default settings on error
      setDraftSettings(DEFAULT_SETTINGS);
      setPublishedSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleThemeChange = (theme: ThemeSettings) => {
    setDraftSettings(prev => ({ ...prev, theme }));
    setHasUnsavedChanges(true);
  };

  const handleContentChange = (content: ContentSettings) => {
    setDraftSettings(prev => ({ ...prev, content }));
    setHasUnsavedChanges(true);
  };

  const handleImagesChange = (images: ImageSettings) => {
    setDraftSettings(prev => ({ ...prev, images }));
    setHasUnsavedChanges(true);
  };

  const handleCSSChange = (customCSS: string) => {
    setDraftSettings(prev => ({ ...prev, customCSS }));
    setHasUnsavedChanges(true);
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      
      // Prepare settings without id field (backend will set it)
      const { id, version, createdAt, updatedAt, publishedAt, publishedBy, ...settingsToSave } = draftSettings;
      
      const response = await ApiService.saveSiteSettings(settingsToSave, 'draft');
      
      if (response.success) {
        // Update draft settings with the response data
        setDraftSettings(response.data.settings);
        setHasUnsavedChanges(false);
        setSnackbar({
          open: true,
          message: 'Draft saved successfully',
          severity: 'success'
        });
      } else {
        throw new Error(response.error || 'Failed to save draft');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to save draft',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setIsPublishing(true);
      
      // First, save the draft if there are unsaved changes
      if (hasUnsavedChanges) {
        const { id, version, createdAt, updatedAt, publishedAt, publishedBy, ...settingsToSave } = draftSettings;
        const saveResponse = await ApiService.saveSiteSettings(settingsToSave, 'draft');
        
        if (!saveResponse.success) {
          throw new Error('Failed to save draft before publishing');
        }
        
        setDraftSettings(saveResponse.data.settings);
        setHasUnsavedChanges(false);
      }
      
      // Now publish the draft
      const response = await ApiService.publishSiteSettings('Settings published from admin console');
      
      if (response.success) {
        // Invalidate cache so guests get fresh settings
        invalidateSiteSettingsCache();
        
        // Update published settings to match draft
        const publishedVersion = {
          ...draftSettings,
          id: 'published',
          version: response.data.version,
          publishedAt: response.data.publishedAt,
          updatedAt: response.data.publishedAt
        };
        
        setPublishedSettings(publishedVersion);
        setShowPublishDialog(false);
        setSnackbar({
          open: true,
          message: 'Settings published successfully! Changes are now live for all guests.',
          severity: 'success'
        });
        
        // Reload settings to ensure we have the latest from the server
        await loadSettings();
      } else {
        throw new Error(response.error || 'Failed to publish settings');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to publish settings',
        severity: 'error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleRevert = async () => {
    try {
      // Restore draft to match published version
      const revertedDraft = {
        ...publishedSettings,
        id: 'draft',
        publishedAt: undefined,
        publishedBy: undefined
      };
      
      setDraftSettings(revertedDraft);
      setHasUnsavedChanges(false);
      setShowRevertDialog(false);
      
      // Save the reverted state to the backend
      const { id, version, createdAt, updatedAt, publishedAt, publishedBy, ...settingsToSave } = revertedDraft;
      await ApiService.saveSiteSettings(settingsToSave, 'draft');
      
      setSnackbar({
        open: true,
        message: 'Changes reverted to last published version',
        severity: 'info'
      });
    } catch (err: any) {
      console.error('Error reverting changes:', err);
      // Still update UI even if save fails
      setDraftSettings({
        ...publishedSettings,
        id: 'draft',
        publishedAt: undefined,
        publishedBy: undefined
      });
      setHasUnsavedChanges(false);
      setShowRevertDialog(false);
      
      setSnackbar({
        open: true,
        message: 'Changes reverted locally. Save to persist.',
        severity: 'info'
      });
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleReset = async () => {
    try {
      setIsResetting(true);
      
      const response = await ApiService.resetSiteSettings();
      
      if (response.success) {
        // Update both draft and published settings with the reset defaults
        setDraftSettings(response.data.draft);
        setPublishedSettings(response.data.published);
        setHasUnsavedChanges(false);
        setShowResetDialog(false);
        
        setSnackbar({
          open: true,
          message: `Settings reset to default successfully. ${response.data.imagesDeleted > 0 ? `${response.data.imagesDeleted} custom image(s) deleted.` : ''}`,
          severity: 'success'
        });
        
        // Reload settings to ensure we have the latest from the server
        await loadSettings();
      } else {
        throw new Error(response.error || 'Failed to reset settings');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to reset settings',
        severity: 'error'
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleRestoreAndPublish = async (versionId: string) => {
    try {
      const response = await ApiService.restoreSiteSettingsVersion(versionId);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Version restored and published successfully!',
          severity: 'success'
        });
        
        // Reload settings to get the restored version
        await loadSettings();
        setHasUnsavedChanges(false);
      } else {
        throw new Error(response.error || 'Failed to restore version');
      }
    } catch (err: any) {
      throw err; // Re-throw to be handled by VersionHistoryViewer
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Site Customization
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UndoIcon />}
            onClick={() => setShowRevertDialog(true)}
            disabled={!hasUnsavedChanges}
          >
            Revert
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => setShowPublishDialog(true)}
            disabled={isPublishing || isSaving}
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </Button>
        </Box>
      </Box>

      {hasUnsavedChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Remember to save your draft before publishing.
        </Alert>
      )}
      
      {!hasUnsavedChanges && draftSettings.updatedAt && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Draft last saved: {new Date(draftSettings.updatedAt).toLocaleString()}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="site customization tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Theme" {...a11yProps(0)} />
            <Tab label="Content" {...a11yProps(1)} />
            <Tab label="Images" {...a11yProps(2)} />
            <Tab label="Advanced CSS" {...a11yProps(3)} />
            <Tab label="Version History" {...a11yProps(4)} />
          </Tabs>
        </Box>
        
        <TabPanel value={currentTab} index={0}>
          <ThemeEditor
            settings={draftSettings.theme}
            onChange={handleThemeChange}
            onPreview={handlePreview}
          />
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          <ContentEditor
            content={draftSettings.content}
            onChange={handleContentChange}
          />
        </TabPanel>
        
        <TabPanel value={currentTab} index={2}>
          <ImageUploader
            images={draftSettings.images}
            onChange={handleImagesChange}
          />
        </TabPanel>
        
        <TabPanel value={currentTab} index={3}>
          <CustomCSSEditor
            css={draftSettings.customCSS || ''}
            onChange={handleCSSChange}
          />
        </TabPanel>
        
        <TabPanel value={currentTab} index={4}>
          <VersionHistoryViewer
            onRestore={(settings) => {
              setDraftSettings(settings);
              setHasUnsavedChanges(true);
            }}
            onRestoreAndPublish={handleRestoreAndPublish}
          />
        </TabPanel>
      </Paper>

      {/* Reset to Default Section */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reset to Default
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reset all customizations to default values. This will delete all custom images, clear custom CSS, and restore default theme and content.
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setShowResetDialog(true)}
          disabled={isResetting}
        >
          Reset to Default
        </Button>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Preview</DialogTitle>
        <DialogContent>
          <SiteCustomizationPreview settings={draftSettings} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <Dialog
        open={showRevertDialog}
        onClose={() => setShowRevertDialog(false)}
      >
        <DialogTitle>Revert Changes?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to revert all changes? This will discard your current draft and restore the last published version.
          </Typography>
          {publishedSettings.publishedAt && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Last published: {new Date(publishedSettings.publishedAt).toLocaleString()}
              {publishedSettings.publishedBy && ` by ${publishedSettings.publishedBy}`}
            </Alert>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRevertDialog(false)}>Cancel</Button>
          <Button onClick={handleRevert} color="warning" variant="contained">
            Revert to Published
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={showPublishDialog}
        onClose={() => !isPublishing && setShowPublishDialog(false)}
      >
        <DialogTitle>Publish Changes?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to publish these changes? They will be visible to all guests immediately.
          </Typography>
          {hasUnsavedChanges && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your unsaved changes will be saved automatically before publishing.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            This action will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Create a version snapshot for history</li>
            <li>Update the published settings</li>
            <li>Invalidate the CloudFront cache</li>
            <li>Make changes visible to all guests</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPublishDialog(false)} disabled={isPublishing}>
            Cancel
          </Button>
          <Button 
            onClick={handlePublish} 
            color="primary" 
            variant="contained"
            disabled={isPublishing}
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={showResetDialog}
        onClose={() => !isResetting && setShowResetDialog(false)}
      >
        <DialogTitle>Reset to Default?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
          <Typography gutterBottom>
            Are you sure you want to reset all customizations to default values?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Restore default theme colors and fonts</li>
            <li>Restore default welcome message and content</li>
            <li>Delete all custom uploaded images from storage</li>
            <li>Clear all custom CSS</li>
            <li>Reset both draft and published versions</li>
            <li>Make default settings visible to all guests immediately</li>
          </Box>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            All current customizations will be permanently lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)} disabled={isResetting}>
            Cancel
          </Button>
          <Button 
            onClick={handleReset} 
            color="error" 
            variant="contained"
            disabled={isResetting}
          >
            {isResetting ? 'Resetting...' : 'Reset to Default'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
